import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shirt, Heart, Check, X, ShoppingBag, Trash2, Plus, Sparkles,
  Search, Tag,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHOP_LINKS = [
  { name: 'Google',     icon: '🔍', url: (q) => `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(q)}` },
  { name: 'H&M',        icon: '🇸🇪', url: (q) => `https://www2.hm.com/sv_se/sok-resultat.html?q=${encodeURIComponent(q)}` },
  { name: 'Lyko',       icon: '💄', url: (q) => `https://www.lyko.com/sv/sok?q=${encodeURIComponent(q)}` },
  { name: 'Lindex',     icon: '🌷', url: (q) => `https://www.lindex.com/se/search?q=${encodeURIComponent(q)}` },
  { name: 'Gina Tricot',icon: '💕', url: (q) => `https://www.ginatricot.com/se/search?q=${encodeURIComponent(q)}` },
  { name: 'Zalando',    icon: '🛍️', url: (q) => `https://www.zalando.se/catalog/?q=${encodeURIComponent(q)}` },
  { name: 'Tradera',    icon: '♻️', url: (q) => `https://www.tradera.com/search?q=${encodeURIComponent(q)}` },
  { name: 'Sellpy',     icon: '🌿', url: (q) => `https://www.sellpy.se/search?q=${encodeURIComponent(q)}` },
];

const STATUS_FILTERS = [
  { key: 'all',      label: 'Allt',         color: '#FDF0D5' },
  { key: 'wishlist', label: 'Önskelista',   color: '#FFC8DD' },
  { key: 'owned',    label: 'Ägda',         color: '#B9FBC0' },
];

const CATEGORY_EMOJI = {
  topp: '👕', byxa: '👖', klanning: '👗', ytterklader: '🧥',
  sko: '👟', accessoar: '👜', underklader: '🩱', hemsaker: '🛍️', annat: '✨',
};

function ItemCard({ item, onOpen, onToggleStatus }) {
  const isOwned = item.status === 'owned';
  return (
    <div
      className={`neo-card p-3 text-left flex flex-col gap-2 transition ${isOwned ? 'opacity-75' : ''}`}
      data-testid="wardrobe-card"
      style={{
        background: item.color_hex || 'white',
      }}
    >
      <button
        onClick={() => onOpen(item)}
        className="text-left flex-1 flex flex-col"
      >
        {/* Visual swatch */}
        <div
          className="aspect-[3/4] rounded-xl border-[3px] border-black flex items-center justify-center text-4xl shadow-[2px_2px_0_#000] mb-2"
          style={{ background: item.color_hex || '#FFFEF5' }}
        >
          <span aria-hidden>{CATEGORY_EMOJI[item.category] || '✨'}</span>
        </div>
        <h4 className={`font-bold text-sm leading-tight ${isOwned ? 'line-through' : ''}`}>{item.item_type}</h4>
        {item.detail && (
          <p className="text-[11px] text-black/75 leading-snug line-clamp-2 mt-0.5">{item.detail}</p>
        )}
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {item.occasion && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-white">
              {item.occasion}
            </span>
          )}
          {item.archetype && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-white/70">
              {item.archetype}
            </span>
          )}
        </div>
      </button>
      {/* Status toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleStatus(item); }}
        className={`neo-btn px-2 py-1 text-[11px] font-bold flex items-center justify-center gap-1 ${isOwned ? 'bg-[#B9FBC0]' : 'bg-white'}`}
        data-testid="wardrobe-toggle-status"
      >
        {isOwned ? (
          <><Check className="w-3 h-3" /> Ägd</>
        ) : (
          <><Heart className="w-3 h-3" /> Önskar</>
        )}
      </button>
    </div>
  );
}

function ItemDetailModal({ item, onClose, onUpdate, onDelete }) {
  if (!item) return null;
  const query = `${item.item_type} ${item.detail || ''} ${item.color_hex ? '' : ''}`.trim();
  const isOwned = item.status === 'owned';

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto"
      onClick={onClose}
      data-testid="wardrobe-detail-modal"
    >
      <div className="w-full max-w-md my-6 neo-card bg-white p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
            {CATEGORY_EMOJI[item.category]} {item.item_type}
          </h2>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Stäng" data-testid="wardrobe-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div
          className="aspect-[3/4] rounded-xl border-[3px] border-black flex items-center justify-center text-7xl mb-3"
          style={{ background: item.color_hex || '#FFFEF5' }}
        >
          {CATEGORY_EMOJI[item.category]}
        </div>

        {item.detail && <p className="text-sm leading-relaxed mb-2">{item.detail}</p>}

        {item.brand_hint && (
          <div className="neo-card p-2 bg-[#FDF0D5] mb-3">
            <p className="text-[10px] font-bold opacity-60">🇸🇪 HITTAS HOS</p>
            <p className="text-sm">{item.brand_hint}</p>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap mb-3">
          {item.occasion && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#FFE8A3]">{item.occasion}</span>}
          {item.archetype && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#C8B6FF]">{item.archetype}</span>}
          {item.color_hex && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-white inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-full border border-black" style={{ background: item.color_hex }} />
              {item.color_hex}
            </span>
          )}
        </div>

        <p className="text-xs font-bold mb-1.5">🛍️ Sök hos</p>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {SHOP_LINKS.map((s) => (
            <a
              key={s.name}
              href={s.url(query)}
              target="_blank"
              rel="noreferrer"
              className="neo-btn px-2 py-1.5 bg-white text-xs font-bold flex items-center gap-1 hover:bg-[#FDF0D5]"
              data-testid={`wardrobe-shop-${s.name}`}
            >
              <span>{s.icon}</span>
              <span>{s.name}</span>
            </a>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onUpdate(item.id, { status: isOwned ? 'wishlist' : 'owned' })}
            className={`neo-btn flex-1 py-2 font-bold text-sm flex items-center justify-center gap-1 ${isOwned ? 'bg-white' : 'bg-[#B9FBC0]'}`}
            data-testid="wardrobe-detail-toggle"
          >
            {isOwned ? <><Heart className="w-4 h-4" /> Tillbaka till önskelista</> : <><Check className="w-4 h-4" /> Markera som köpt</>}
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="neo-btn px-3 py-2 bg-white text-red-600 font-bold text-sm"
            aria-label="Ta bort"
            data-testid="wardrobe-detail-delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AddItemModal({ onClose, onSaved }) {
  const [itemType, setItemType] = useState('');
  const [detail, setDetail] = useState('');
  const [color, setColor] = useState('#FFC8DD');
  const [brand, setBrand] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!itemType.trim()) { toast.error('Ange plaggtyp'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/wardrobe`, {
        item_type: itemType,
        detail: detail || null,
        color_hex: color,
        brand_hint: brand || null,
        status: 'wishlist',
      }, { withCredentials: true });
      toast.success('💕 Tillagd i önskelistan');
      onSaved();
      onClose();
    } catch {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-sm p-3 flex items-center justify-center" onClick={onClose} data-testid="wardrobe-add-modal">
      <div className="w-full max-w-sm neo-card bg-white p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-1.5"><Plus className="w-4 h-4" /> Lägg till plagg</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2">
          <input type="text" placeholder="Plaggtyp (t.ex. Vit linne)" value={itemType} onChange={(e) => setItemType(e.target.value)} className="neo-input w-full" data-testid="wardrobe-add-type" />
          <textarea placeholder="Detaljer (valfritt)" value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} className="neo-input w-full" data-testid="wardrobe-add-detail" />
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold">Färg:</label>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-12 h-8 border-2 border-black rounded cursor-pointer" data-testid="wardrobe-add-color" />
          </div>
          <input type="text" placeholder="Var hittas det? (valfritt)" value={brand} onChange={(e) => setBrand(e.target.value)} className="neo-input w-full" />
          <button onClick={save} disabled={saving} className="neo-btn w-full py-2 bg-[#FFC8DD] font-bold disabled:opacity-60" data-testid="wardrobe-add-save">
            {saving ? 'Sparar...' : '💕 Lägg till'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WardrobePage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, wishlist: 0, owned: 0 });
  const [categories, setCategories] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [active, setActive] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [outfits, setOutfits] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const { data } = await axios.get(`${API}/wardrobe`, { withCredentials: true, params });
      setItems(data.items || []);
      setCounts(data.counts || { all: 0, wishlist: 0, owned: 0 });
      setCategories(data.categories || []);
    } catch (e) { console.error(e); }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchOutfits = async () => {
    try {
      const { data } = await axios.get(`${API}/makeup/entries`, { withCredentials: true });
      setOutfits((data.entries || []).filter((e) => e.analysis));
    } catch { /* ignore */ }
  };

  const toggleStatus = async (item) => {
    try {
      await axios.patch(`${API}/wardrobe/${item.id}`, {
        status: item.status === 'owned' ? 'wishlist' : 'owned',
      }, { withCredentials: true });
      toast.success(item.status === 'owned' ? '💕 Tillbaka i önskelistan' : '✨ Du har det nu!');
      if (active?.id === item.id) setActive({ ...item, status: item.status === 'owned' ? 'wishlist' : 'owned' });
      fetchAll();
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const updateItem = async (id, updates) => {
    try {
      await axios.patch(`${API}/wardrobe/${id}`, updates, { withCredentials: true });
      if (active?.id === id) setActive({ ...active, ...updates });
      fetchAll();
      if (updates.status === 'owned') toast.success('✨ Du har det nu!');
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Ta bort plagget?')) return;
    try {
      await axios.delete(`${API}/wardrobe/${id}`, { withCredentials: true });
      setActive(null);
      fetchAll();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const importFromOutfit = async (entryId) => {
    try {
      const { data } = await axios.post(`${API}/wardrobe/extract/${entryId}`, {}, { withCredentials: true });
      toast.success(`👗 Importerade ${data.added} plagg`);
      setShowImport(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte importera');
    }
  };

  return (
    <div className="space-y-4" data-testid="wardrobe-page">
      <header className="neo-card p-5 bg-gradient-to-br from-[#FFE8F5] via-[#FDF0D5] to-[#E8F0FF] relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="font-['Fredoka'] font-bold text-xl flex items-center gap-2">
            <Shirt className="w-5 h-5" /> Garderob
          </h2>
          <p className="text-sm text-black/75 mt-1 max-w-xl">
            Din visuella kapselgarderob. Lägg till, klicka för att hitta i butik, kryssa när du köpt. 📌👗
          </p>
        </div>
        <div className="absolute -right-4 -bottom-4 text-7xl opacity-10">👗</div>
      </header>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap" data-testid="wardrobe-actions">
        <button
          onClick={() => setShowAdd(true)}
          className="neo-btn px-3 py-2 bg-[#FFC8DD] font-bold text-sm flex items-center gap-1"
          data-testid="wardrobe-add-btn"
        >
          <Plus className="w-4 h-4" /> Lägg till
        </button>
        <button
          onClick={() => { setShowImport(true); fetchOutfits(); }}
          className="neo-btn px-3 py-2 bg-[#C8B6FF] font-bold text-sm flex items-center gap-1"
          data-testid="wardrobe-import-btn"
        >
          <Sparkles className="w-4 h-4" /> Importera från sminkguide
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap" data-testid="wardrobe-status-filter">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            className={`neo-btn px-3 py-1.5 text-xs font-bold ${statusFilter === s.key ? '' : 'opacity-70'}`}
            style={{ background: statusFilter === s.key ? s.color : 'white' }}
            data-testid={`wardrobe-status-${s.key}`}
          >
            {s.label} ({counts[s.key] || 0})
          </button>
        ))}
      </div>

      {/* Category pills */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="wardrobe-category-filter">
          <button
            onClick={() => setCategoryFilter('')}
            className={`neo-btn px-2 py-1 text-[11px] shrink-0 ${!categoryFilter ? 'bg-[#FFE8A3]' : 'bg-white'}`}
          >
            Alla kategorier
          </button>
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategoryFilter(c.key === categoryFilter ? '' : c.key)}
              className={`neo-btn px-2 py-1 text-[11px] shrink-0 flex items-center gap-1 ${categoryFilter === c.key ? 'bg-[#FFE8A3]' : 'bg-white'}`}
              data-testid={`wardrobe-cat-${c.key}`}
            >
              {CATEGORY_EMOJI[c.key]} {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Pinterest-style grid */}
      {items.length === 0 ? (
        <div className="neo-card p-8 bg-white text-center">
          <Shirt className="w-10 h-10 mx-auto mb-2 text-[#FFC8DD]" />
          <h3 className="font-bold">Tomt än så länge</h3>
          <p className="text-sm text-black/70 mt-1">
            Klicka "Lägg till" för att börja, eller "Importera från sminkguide" för att fylla med dina outfit-förslag automatiskt 💕
          </p>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="wardrobe-grid">
          {items.map((it) => (
            <ItemCard
              key={it.id}
              item={it}
              onOpen={setActive}
              onToggleStatus={toggleStatus}
            />
          ))}
        </div>
      )}

      {active && (
        <ItemDetailModal
          item={active}
          onClose={() => setActive(null)}
          onUpdate={updateItem}
          onDelete={deleteItem}
        />
      )}

      {showAdd && (
        <AddItemModal onClose={() => setShowAdd(false)} onSaved={fetchAll} />
      )}

      {showImport && (
        <div className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto" onClick={() => setShowImport(false)} data-testid="wardrobe-import-modal">
          <div className="w-full max-w-md my-6 neo-card bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Importera från sminkguide</h3>
              <button onClick={() => setShowImport(false)} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-black/70 mb-3">Välj en sminkguide som har outfit-förslag — alla plagg läggs till i din önskelista.</p>
            {outfits.filter((o) => o.analysis).length === 0 ? (
              <p className="text-sm text-black/60 py-4 text-center">Inga sminkguider med outfit än. Gå till Sminkspegel → öppna en guide → "Få klädarketyp + outfit-förslag" först.</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {outfits.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => importFromOutfit(o.id)}
                    className="w-full neo-card p-3 bg-white text-left hover:bg-[#FDF0D5] transition"
                    data-testid="wardrobe-import-option"
                  >
                    <p className="font-bold text-sm">{o.style_emoji} {o.style_name}</p>
                    <p className="text-[11px] text-black/60 line-clamp-2">{o.analysis?.observation}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
